import { useEffect, useState, useCallback } from "react";
import api, { getToken } from "../lib/api";
import { toast } from "sonner";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import {
  Wallet, Plus, Trash2, X, TrendingUp, TrendingDown, Receipt, PieChart, Landmark, Upload, Loader2,
} from "lucide-react";

const euro = (n) => (n || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
const imgSrc = (url) => (url && url.includes("/api/files/")) ? `${url}${url.includes("?") ? "&" : "?"}auth=${getToken()}` : url;

const CATEGORIES = [
  { key: "matieres", label: "Matières premières" },
  { key: "fournitures", label: "Fournitures" },
  { key: "outillage", label: "Outillage" },
  { key: "sous_traitance", label: "Sous-traitance" },
  { key: "loyer", label: "Loyer / local" },
  { key: "vehicule", label: "Véhicule" },
  { key: "energie", label: "Énergie" },
  { key: "salaires", label: "Salaires" },
  { key: "marketing", label: "Marketing" },
  { key: "assurance", label: "Assurance" },
  { key: "logiciels", label: "Logiciels" },
  { key: "autre", label: "Autre" },
];
const CAT_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.key, c.label]));
const SOURCES = [
  { key: "manuel", label: "Saisie manuelle" },
  { key: "scan", label: "Facture scannée" },
  { key: "ticket", label: "Ticket de caisse" },
  { key: "banque", label: "Synchronisation bancaire" },
];

const PERIODS = [
  { key: "week", label: "Semaine" },
  { key: "month", label: "Mois" },
  { key: "year", label: "Année" },
];

function DeltaChip({ delta, invert }) {
  const up = delta >= 0;
  const good = invert ? !up : up;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${good ? "text-emerald-600" : "text-rose-500"}`}>
      {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
      {up ? "+" : ""}{delta}%
    </span>
  );
}

export default function Expenses() {
  const [tab, setTab] = useState("rentabilite");
  const [period, setPeriod] = useState("month");
  const [prof, setProf] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const blank = { date: new Date().toISOString().slice(0, 10), category: "matieres", source: "manuel", supplier: "", amount_ht: "", amount_ttc: "", vat_rate: 20, note: "", document_id: "", attachment: null };
  const [form, setForm] = useState(blank);

  const loadProf = useCallback(() => {
    api.get(`/dashboard/profitability?period=${period}`).then((r) => setProf(r.data)).catch(() => {});
  }, [period]);
  const loadExpenses = () => api.get("/expenses").then((r) => setExpenses(r.data)).catch(() => {});

  useEffect(() => { loadProf(); }, [loadProf]);
  useEffect(() => {
    loadExpenses();
    api.get("/documents").then((r) => setDocuments(r.data)).catch(() => {});
  }, []);

  const setHT = (v) => {
    const ht = parseFloat(v) || 0;
    setForm((f) => ({ ...f, amount_ht: v, amount_ttc: ht ? (ht * (1 + (parseFloat(f.vat_rate) || 0) / 100)).toFixed(2) : "" }));
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, attachment: data }));
      toast.success("Justificatif ajouté");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'upload");
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.date || !(parseFloat(form.amount_ht) > 0)) return toast.error("Date et montant HT requis");
    await api.post("/expenses", {
      date: form.date, category: form.category, source: form.source, supplier: form.supplier,
      amount_ht: parseFloat(form.amount_ht) || 0, amount_ttc: parseFloat(form.amount_ttc) || parseFloat(form.amount_ht) || 0,
      vat_rate: parseFloat(form.vat_rate) || 0, note: form.note,
      document_id: form.document_id || null, attachment: form.attachment,
    });
    toast.success("Dépense enregistrée");
    setShowForm(false);
    setForm(blank);
    loadExpenses();
    loadProf();
  };

  const remove = async (id) => {
    await api.delete(`/expenses/${id}`);
    loadExpenses();
    loadProf();
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-navy flex items-center justify-center"><Wallet className="w-5 h-5 text-sky-400" /></div>
          <div>
            <h1 className="text-3xl font-display font-bold text-navy">Dépenses & Rentabilité</h1>
            <p className="text-slate-500">Pilotez votre CA, vos coûts et votre marge</p>
          </div>
        </div>
        <div className="flex bg-slate-100 rounded-xl p-1" data-testid="expense-period">
          {PERIODS.map((p) => (
            <button key={p.key} onClick={() => setPeriod(p.key)} data-testid={`exp-period-${p.key}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${period === p.key ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-navy"}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        {[["rentabilite", "Rentabilité"], ["depenses", "Dépenses"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`exp-tab-${k}`}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${tab === k ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-sky-300"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "rentabilite" && (
        <div className="space-y-6" data-testid="rentabilite-panel">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="prof-ca">
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><TrendingUp className="w-5 h-5" /></div>
                {typeof prof?.ca_delta === "number" && <DeltaChip delta={prof.ca_delta} />}
              </div>
              <div className="mt-3 text-3xl font-display font-bold text-navy tabular">{euro(prof?.ca)}</div>
              <div className="text-sm text-slate-500 mt-0.5">Chiffre d'affaires (HT)</div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="prof-expenses">
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center"><Receipt className="w-5 h-5" /></div>
                {typeof prof?.expenses_delta === "number" && <DeltaChip delta={prof.expenses_delta} invert />}
              </div>
              <div className="mt-3 text-3xl font-display font-bold text-navy tabular">{euro(prof?.expenses)}</div>
              <div className="text-sm text-slate-500 mt-0.5">Dépenses (HT)</div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="prof-marge">
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center"><PieChart className="w-5 h-5" /></div>
                {typeof prof?.marge_delta === "number" && <DeltaChip delta={prof.marge_delta} />}
              </div>
              <div className="mt-3 text-3xl font-display font-bold text-navy tabular">{euro(prof?.marge)}</div>
              <div className="text-sm text-slate-500 mt-0.5">Marge · {prof?.marge_pct ?? 0} %</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6 min-w-0">
            <h3 className="font-display font-semibold text-navy text-lg">CA vs Dépenses vs Marge</h3>
            <div className="h-72 mt-4 min-w-0">
              <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                <BarChart data={prof?.series || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={48} />
                  <Tooltip formatter={(v) => euro(v)} />
                  <Legend />
                  <Bar dataKey="ca" name="CA" fill="#10B981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expenses" name="Dépenses" fill="#F43F5E" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="marge" name="Marge" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-display font-semibold text-navy text-lg">Dépenses par catégorie</h3>
            <div className="mt-4 space-y-2">
              {(prof?.by_category || []).map((c) => {
                const max = prof.by_category[0]?.amount || 1;
                return (
                  <div key={c.category} data-testid={`cat-${c.category}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-slate-600">{CAT_LABEL[c.category] || c.category}</span>
                      <span className="font-semibold text-navy tabular">{euro(c.amount)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full bg-sky-400 rounded-full" style={{ width: `${(c.amount / max) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
              {(!prof?.by_category || prof.by_category.length === 0) && <p className="text-sm text-slate-400 py-6 text-center">Aucune dépense sur cette période.</p>}
            </div>
          </div>

          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 flex items-center gap-3" data-testid="bridge-banner">
            <Landmark className="w-5 h-5 text-slate-400 shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-medium text-navy">Synchronisation bancaire via Bridge</div>
              <div className="text-xs text-slate-500">Importez automatiquement vos dépenses depuis votre banque — bientôt disponible.</div>
            </div>
            <button disabled className="px-3 py-2 rounded-xl bg-slate-200 text-slate-500 text-sm font-semibold cursor-not-allowed">Bientôt</button>
          </div>
        </div>
      )}

      {tab === "depenses" && (
        <div className="space-y-4" data-testid="depenses-panel">
          <div className="flex justify-end">
            <button onClick={() => { setForm(blank); setShowForm(true); }} data-testid="new-expense-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sky-400 text-navy font-semibold hover:bg-sky-500 transition-colors">
              <Plus className="w-4 h-4" /> Ajouter une dépense
            </button>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Catégorie</th>
                  <th className="text-left px-4 py-3 hidden sm:table-cell">Fournisseur</th>
                  <th className="text-right px-4 py-3">HT</th>
                  <th className="text-right px-4 py-3 hidden sm:table-cell">TTC</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {expenses.map((e) => (
                  <tr key={e.id} className="hover:bg-slate-50" data-testid={`expense-row-${e.id}`}>
                    <td className="px-4 py-3 font-mono text-slate-600">{e.date}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium">{CAT_LABEL[e.category] || e.category}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 hidden sm:table-cell">{e.supplier || "—"}</td>
                    <td className="px-4 py-3 text-right font-semibold text-navy tabular">{euro(e.amount_ht)}</td>
                    <td className="px-4 py-3 text-right text-slate-500 tabular hidden sm:table-cell">{euro(e.amount_ttc)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => remove(e.id)} data-testid={`delete-expense-${e.id}`} className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
                {expenses.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">Aucune dépense enregistrée.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="expense-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-display font-bold text-navy">Nouvelle dépense</h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-500">Date</label>
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="expense-date"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Catégorie</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="expense-category"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-sky-400">
                  {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-500">Origine de la saisie</label>
                <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} data-testid="expense-source"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-sky-400">
                  {SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-500">Fournisseur</label>
                <input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} data-testid="expense-supplier" placeholder="Nom du fournisseur"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Montant HT (€)</label>
                <input type="number" value={form.amount_ht} onChange={(e) => setHT(e.target.value)} data-testid="expense-ht" placeholder="0.00"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 font-mono focus:outline-none focus:ring-2 focus:ring-sky-400" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Montant TTC (€)</label>
                <input type="number" value={form.amount_ttc} onChange={(e) => setForm({ ...form, amount_ttc: e.target.value })} data-testid="expense-ttc" placeholder="0.00"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 font-mono focus:outline-none focus:ring-2 focus:ring-sky-400" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-500">Lier à un document (optionnel)</label>
                <select value={form.document_id} onChange={(e) => setForm({ ...form, document_id: e.target.value })} data-testid="expense-document"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-sky-400">
                  <option value="">Aucun</option>
                  {documents.map((d) => <option key={d.id} value={d.id}>{d.number} — {d.title || d.client_name}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-500">Note</label>
                <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} data-testid="expense-note"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-500">Justificatif (optionnel)</label>
                {form.attachment ? (
                  <div className="mt-1 flex items-center gap-2">
                    <img src={imgSrc(form.attachment.url)} alt="justificatif" className="w-12 h-12 rounded-lg object-cover border border-slate-200" />
                    <button onClick={() => setForm({ ...form, attachment: null })} className="text-xs text-rose-500 hover:underline">Retirer</button>
                  </div>
                ) : (
                  <label className="mt-1 flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed border-slate-300 text-slate-500 text-sm cursor-pointer hover:border-sky-400" data-testid="expense-upload">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Joindre une image
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} />
                  </label>
                )}
              </div>
            </div>
            <button onClick={save} data-testid="save-expense"
              className="mt-5 w-full py-3 rounded-xl bg-navy text-white font-semibold hover:bg-navy-deep transition-colors">
              Enregistrer la dépense
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
